import Badge20 from '@carbon/icons-react/es/badge/20';
import { useNavigate } from '@rocketlane/rocket-router';
import { Row, Col, Typography } from 'antd';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { SupportedPlansType } from 'api/billing/types';
import { ReactComponent as Diamond } from 'assets/svgs/diamond.svg';
import { Modal as ModalAtom } from 'components/atoms/modal';
import { Button, LinkButton } from 'components/facade/button';
import { ROUTE_PATHS } from 'routes/routes-paths';

const Modal = styled(ModalAtom)`
  .ant-modal-body {
    padding: 0;
  }
  padding: 0;
`;

const ModalContentStyled = styled(Row)`
  display: flex;
  flex-direction: row;
  background-color: var(--white-100);
  border-radius: var(--border-radius-small);
  overflow: hidden;
  height: 380px;
`;

const LeftCol = styled(Col)`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--coolest-gray-30);
`;

const TypographyText = styled(Typography.Text)``;

const RightCol = styled(Col)`
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  width: 104px;
  gap: var(--spacing-03);

  ${TypographyText} {
    font-size: var(--font-size-base);
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--spacing-05);
`;

const UpgradeButton = styled(Button)`
  justify-content: flex-start;
  margin-top: var(--spacing-05);
  margin-bottom: var(--spacing-05);
`;

const ButtonText = styled.span`
  text-align: left;
  flex-grow: 1;
`;

const LearnMoreButton = styled(LinkButton)`
  align-self: flex-start;
`;

const ModalTitle = styled(Typography.Title)`
  font-size: var(--heading-5-size);
  width: 240px;
`;

const ModalDescription = styled(Typography.Text)`
  flex-grow: 1;
  font-size: var(--font-size-base);
  width: 240px;
`;

export const getIcon = (planType: SupportedPlansType) => {
  if (
    [
      SupportedPlansType.Enterprise,
      SupportedPlansType['Enterprise-V2'],
    ].includes(planType)
  ) {
    return Badge20;
  }

  return Diamond;
};

export const getColor = (planType: SupportedPlansType) => {
  if (
    [
      SupportedPlansType.Enterprise,
      SupportedPlansType['Enterprise-V2'],
    ].includes(planType)
  ) {
    return 'var(--green-40)';
  }

  return 'var(--warning-border-color)';
};

export const FeatureGatingModal = ({
  planType,
  title,
  description,
  featureImage,
  learnMoreLink,
}: {
  planType: SupportedPlansType;
  title: string;
  description: string;
  featureImage: any;
  learnMoreLink?: string;
}) => {
  const Icon = getIcon(planType);
  const color = getColor(planType);
  const navigate = useNavigate();

  return (
    <Modal centered visible width={824} closable={false} footer={null}>
      <ModalContentStyled>
        <LeftCol span={14}>
          <img
            src={featureImage}
            alt="Feature Screenshot"
            style={{ width: '80%' }}
          />
        </LeftCol>
        <RightCol span={10}>
          <Header>
            <HeaderLeft>
              <Icon fill={color} currentColor={color} color={color} />
              <TypographyText style={{ color }}>
                <FormattedMessage
                  id={`billing.plan.${planType.toLowerCase()}`}
                />
              </TypographyText>
            </HeaderLeft>
          </Header>
          <ModalTitle>{title}</ModalTitle>
          <ModalDescription>{description}</ModalDescription>
          <UpgradeButton
            iconPlacement="right"
            type="primary"
            icon={<Icon fill="var(--white-100)" color="var(--white-100)" />}
            onClick={() =>
              navigate(
                `/${ROUTE_PATHS.SETTINGS}/${ROUTE_PATHS.SETTINGS_BILLING}`,
              )
            }
          >
            <ButtonText>
              <FormattedMessage
                id={`billing.feature_gating.upgrade_button.${planType.toLowerCase()}`}
              />
            </ButtonText>
          </UpgradeButton>
          {learnMoreLink && (
            <LearnMoreButton href={learnMoreLink} target="_blank">
              <FormattedMessage id="billing.feature_gating.learn_more" />
            </LearnMoreButton>
          )}
        </RightCol>
      </ModalContentStyled>
    </Modal>
  );
};
